import axios from 'axios';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  ADD_CATEGORY_ERROR,
  ADD_CATEGORY_RESPONSE,
  ADD_GIFT_ERROR,
  ADD_GIFT_RESPONSE,
  ADMIN_ONLY_GIFTS_RESPONSE,
  GENERIC_ERROR,
  GENERIC_SUCCESS,
  GET_ADMIN_CATEGORIES_LIST_ERROR,
  GET_ADMIN_CATEGORIES_LIST_RESPONSE,
  GET_ADMIN_GIFT_LIST_ERROR,
  GET_ADMIN_GIFT_LIST_RESPONSE,
  GET_ADMIN_SINGLE_CATEGORY_ERROR,
  GET_ADMIN_SINGLE_CATEGORY_RESPONSE,
  GET_GIFTS_BY_CATEGORIES_ERROR,
  GET_GIFTS_BY_CATEGORIES_SUCCESS,
  GET_GIFT_CATEGORIES_LIST_ERROR,
  GET_GIFT_CATEGORIES_LIST_RESPONSE,
  GET_GIFT_LIST_ERROR,
  GET_GIFT_LIST_RESPONSE,
  GET_GOLD_GIFTS_DATA_ERROR,
  GET_QUICK_GIFTS_DATA_ERROR,
  GET_QUICK_GIFTS_DATA_SUCCESS,
  GET_SINGLE_CATEGORY_ERROR,
  GET_SINGLE_CATEGORY_RESPONSE,
  GET_SORTED_GIFTS,
  GET_SORTED_GIFTS_ERROR,
  GET_SORTED_GIFTS_RESPONSE,
  GIFTING_HISTORY_RESPONSE,
  GIFTS_SEARCH_ERROR,
  GIFTS_SEARCH_RESPONSE,
  SEND_GIFT,
  SEND_GIFT_ERROR,
  SEND_GIFT_SUCCESS,
  SEND_MASS_GIFT_ERROR,
  SEND_MASS_GIFT_RESPONSE,
  SET_GOLD_GIFTS_DATA_SUCCESS,
  SINGLE_GIFT_RESPONSE,
  TMP_IMAGE_RESPONSE,
} from 'services/MessagingService/EventNames';
import { GIFT_GOLD_STAR_NOTIFICATION_EXTRA } from 'enum/helpers';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const ADMIN_CATEGORIES_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/categories`;
const ADMIN_GIFTS_BY_CATEGORIES_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/categories/gifts`;
const ADMIN_GIFTS_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/gifts`;
const ADMIN_ONLY_GIFTS_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/gifts/admin-only`;
const ADMIN_SINGLE_CATEGORY_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/categories/{categoryId}`;
const ADMIN_SINGLE_GIFT_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/gifts/{giftId}`;
const CATEGORIES_URL = `${BASE_URL}/rest/v1.0/giftapi/categories`;
const GIFTING_HISTORY_URL = `${BASE_URL}/rest/v1.0/giftapi/history/sender/{username}`;
const GIFTS_SEARCH_URL = `${BASE_URL}/rest/v1.0/giftapi/gifts/search`;
const GIFTS_URL = `${BASE_URL}/rest/v1.0/giftapi/gifts`;
const GIFT_BY_CATEGORIES_URL = `${BASE_URL}/rest/v1.0/giftapi/categories/gifts`;
const GOLD_GIFT_URL = `${BASE_URL}/rest/v1.0/giftapi/categories/gold`;
const MASS_GIFTS_URL = `${BASE_URL}/rest/v1.0/admin/gift/broadcast`;
const PERFORMER_GIFT_SHOWCASE_URL = `${BASE_URL}/rest/v1.0/profile/{username}/gifts`;
const SEND_GIFT_URL = `${BASE_URL}/rest/v1.0/profile/{username}/gift/send`;
const SINGLE_CATEGORY_URL = `${BASE_URL}/rest/v1.0/giftapi/categories/{categoryId}`;
const TMP_IMAGE_URL = `${BASE_URL}/rest/v1.0/admin/giftapi/gifts/tmp_image_url`;
const GIFT_API_URL = `${BASE_URL}/rest/v1.0/giftapi`;

class GiftsService {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(SEND_GIFT, this.sendGift);
    this.messagingService.on(GET_SORTED_GIFTS, this._getSortedGifts);
  }

  destructor() {
    this.messagingService.off(SEND_GIFT, this.sendGift);
    this.messagingService.off(GET_SORTED_GIFTS, this._getSortedGifts);
  }

  getGiftCategoryList = () => {
    ApiService.get(CATEGORIES_URL)
      .then((response) => {
        this.messagingService.emit(GET_GIFT_CATEGORIES_LIST_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_GIFT_CATEGORIES_LIST_ERROR, error.data);
      });
  };

  getGiftsByCategory = (params) => {
    ApiService.get(GIFT_BY_CATEGORIES_URL, { params })
      .then((response) => {
        this.messagingService.emit(GET_GIFTS_BY_CATEGORIES_SUCCESS, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_GIFTS_BY_CATEGORIES_ERROR, error.data);
      });
  };

  getGiftList = (perfomer, params) => {
    const url = ApiService.substitute(PERFORMER_GIFT_SHOWCASE_URL, { username: encodeURIComponent(perfomer) });
    ApiService.get(url, { params })
      .then((response) => {
        this.messagingService.emit(GET_GIFT_LIST_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_GIFT_LIST_ERROR, error.data);
      });
  };

  getAdminCategoryList = () => {
    ApiService.get(ADMIN_CATEGORIES_URL)
      .then((response) => {
        this.messagingService.emit(GET_ADMIN_CATEGORIES_LIST_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_ADMIN_CATEGORIES_LIST_ERROR, error.data);
      });
  };

  getAdminGiftsByCategory = (params) => {
    const finalParams = { ...params, size: 5000 };
    ApiService.get(ADMIN_GIFTS_BY_CATEGORIES_URL, { params: finalParams })
      .then((response) => {
        this.messagingService.emit(GET_ADMIN_GIFT_LIST_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_ADMIN_GIFT_LIST_ERROR, error.data);
      });
  };

  getAdminGifts = (params) => {
    ApiService.get(ADMIN_GIFTS_URL, { params })
      .then((response) => {
        this.messagingService.emit(GET_ADMIN_GIFT_LIST_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_ADMIN_GIFT_LIST_ERROR, error.data);
      });
  };

  getSingleCategory = (categoryId, params) => {
    const url = ApiService.substitute(ADMIN_SINGLE_CATEGORY_URL, { categoryId: encodeURIComponent(categoryId) });
    ApiService.get(url, { params })
      .then((response) => {
        this.messagingService.emit(GET_ADMIN_SINGLE_CATEGORY_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_ADMIN_SINGLE_CATEGORY_ERROR, error.data);
      });
  };

  getMarketplaceSingleCategory = (categoryId, params) => {
    const url = ApiService.substitute(SINGLE_CATEGORY_URL, { categoryId: encodeURIComponent(categoryId) });
    ApiService.get(url, { params })
      .then((response) => {
        this.messagingService.emit(GET_SINGLE_CATEGORY_RESPONSE, response.data);
      })
      .catch((error) => {
        this.messagingService.emit(GET_SINGLE_CATEGORY_ERROR, error.data);
      });
  };

  getSingleGift = (giftId) => {
    const url = ApiService.substitute(ADMIN_SINGLE_GIFT_URL, { giftId: encodeURIComponent(giftId) });
    ApiService.get(url)
      .then((response) => this.messagingService.emit(SINGLE_GIFT_RESPONSE, response.data))
      .catch((error) => {
        logger.error(error);
        this.messagingService.emit(GENERIC_ERROR);
      });
  };

  addNewGift = (data, params) => {
    const { category, page, sort } = params;
    ApiService.post(ADMIN_GIFTS_URL, { data })
      .then((response) => {
        if (category) {
          this.getSingleCategory(category);
        } else if (sort) {
          this.getAdminGifts({
            page,
            sort: `${sort}${sort === 'releaseDate' ? ',desc' : ''}`,
          });
        } else {
          this.getAdminGiftsByCategory({ page });
        }
        this.messagingService.emit(ADD_GIFT_RESPONSE, response.data);
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.success.message');
      })
      .catch(() => {
        this.messagingService.emit(GENERIC_ERROR);
        this.messagingService.emit(ADD_GIFT_ERROR);
      });
  };

  addNewCategory = (data, params) => {
    const { page, category, sort } = params;
    ApiService.post(ADMIN_CATEGORIES_URL, { data })
      .then((response) => {
        if (category) {
          this.getSingleCategory(category);
        } else if (sort) {
          this.getAdminGifts({
            page,
            sort: `${sort}${sort === 'releaseDate' ? ',desc' : ''}`,
          });
        } else {
          this.getAdminGiftsByCategory({ page });
        }
        this.getAdminCategoryList();
        this.messagingService.emit(ADD_CATEGORY_RESPONSE, response.data);
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.success.message');
      })
      .catch(() => {
        this.messagingService.emit(GENERIC_ERROR);
        this.messagingService.emit(ADD_CATEGORY_ERROR);
      });
  };

  updateCategory = (categoryId, data, params) => {
    const { page, category, sort } = params;
    const url = ApiService.substitute(ADMIN_SINGLE_CATEGORY_URL, { categoryId: encodeURIComponent(categoryId) });
    ApiService.put(url, { data })
      .then((response) => {
        if (category) {
          this.getSingleCategory(category);
        } else if (sort) {
          this.getAdminGifts({
            page,
            sort: `${sort}${sort === 'releaseDate' ? ',desc' : ''}`,
          });
        } else {
          this.getAdminGiftsByCategory({ page });
        }
        this.getAdminCategoryList();
        this.messagingService.emit(ADD_CATEGORY_RESPONSE, response.data);
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.success.message');
      })
      .catch(() => {
        this.messagingService.emit(GENERIC_ERROR);
        this.messagingService.emit(ADD_CATEGORY_ERROR);
      });
  };

  updateGift = (giftId, data, params, searchValue) => {
    const { category, page, sort } = params;
    const url = ApiService.substitute(ADMIN_SINGLE_GIFT_URL, { giftId: encodeURIComponent(giftId) });
    ApiService.put(url, { data })
      .then((response) => {
        if (category) {
          this.getSingleCategory(category);
        } else if (sort) {
          this.getAdminGifts({
            page,
            sort: `${sort}${sort === 'releaseDate' ? ',desc' : ''}`,
          });
        } else {
          this.getAdminGiftsByCategory({ page });
          if (searchValue) {
            this.searchGifts({ text: searchValue });
          }
        }
        this.messagingService.emit(ADD_GIFT_RESPONSE, response.data);
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.success.message');
      })
      .catch(() => {
        this.messagingService.emit(GENERIC_ERROR);
        this.messagingService.emit(ADD_GIFT_ERROR);
      });
  };

  getTmpImageData = (params) => {
    ApiService.get(TMP_IMAGE_URL, { params })
      .then((response) => this.messagingService.emit(TMP_IMAGE_RESPONSE, response.data))
      .catch((error) => logger.error(error));
  };

  uploadImageToCdn = (url, image) => {
    const config = {
      method: 'put',
      url,
      headers: { 'Content-Type': image.type },
      data: image,
    };
    axios(config).catch((error) => logger.error(error));
  };

  sendGift = (payload) => {
    const url = ApiService.substitute(SEND_GIFT_URL, { username: encodeURIComponent(payload.recipientName) });
    const data = { ...payload };
    delete data.recipientName;
    ApiService.post(url, { data })
      .then((res) => {
        this.messagingService.emit(SEND_GIFT_SUCCESS, res);
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.gift.sent.success.toast');
        this.getGiftList(payload.recipientName);
        if (payload.giftId === GIFT_GOLD_STAR_NOTIFICATION_EXTRA) {
          this.getGoldGifts();
        }
      })
      .catch((e) => {
        this.messagingService.emit(SEND_GIFT_ERROR, e);
        this.messagingService.emit(GENERIC_ERROR);
      });
  };

  sendMassGift = (data) => {
    ApiService.post(MASS_GIFTS_URL, { data })
      .then(() => {
        this.messagingService.emit(GENERIC_SUCCESS, 'v2profile.gift.sent.success.toast');
        this.messagingService.emit(SEND_MASS_GIFT_RESPONSE);
      })
      .catch(() => {
        this.messagingService.emit(SEND_MASS_GIFT_ERROR);
        this.messagingService.emit(GENERIC_ERROR);
      });
  };

  getAdminOnlyGifts = () => {
    ApiService.get(ADMIN_ONLY_GIFTS_URL)
      .then((res) => {
        this.messagingService.emit(ADMIN_ONLY_GIFTS_RESPONSE, res.data);
      })
      .catch(() => {
        this.messagingService.emit(GENERIC_ERROR);
      });
  };

  getGoldGifts = () => {
    ApiService.get(GOLD_GIFT_URL)
      .then((response) => {
        this.messagingService.emit(SET_GOLD_GIFTS_DATA_SUCCESS, response.data);
      })
      .catch((err) => {
        this.messagingService.emit(GET_GOLD_GIFTS_DATA_ERROR, err);
      });
  };

  getQuickGifts = (username) => {
    let FEATURED_GIFTS_URL = null;
    const specialGiftsUserName = 'cam4happyhour';

    if (username && username === specialGiftsUserName) {
      FEATURED_GIFTS_URL = `${GIFT_API_URL}/categories/145`;
    } else {
      FEATURED_GIFTS_URL =
        `${GIFT_API_URL}/categories/gifts?size=1&sort=visible,desc&sort=goldExclusive,asc&sort=priority,desc`;
    }

    const POPULAR_GIFTS_URL = `${BASE_URL}/rest/v1.0/giftapi/gifts/popular`;

    const getPopularGiftsCall = ApiService.get(POPULAR_GIFTS_URL);
    const getFeaturedGiftsCall = ApiService.get(FEATURED_GIFTS_URL);
    const getGoldGiftsCall = ApiService.get(GOLD_GIFT_URL);

    Promise.all([getPopularGiftsCall, getFeaturedGiftsCall, getGoldGiftsCall])
      .then((res) => {
        const [popularGifts, featuredGifts, goldGifts] = res;
        this.messagingService.emit(GET_QUICK_GIFTS_DATA_SUCCESS, {
          popular: popularGifts.data,
          featured: username && username === specialGiftsUserName ? [featuredGifts.data] : featuredGifts.data.content,
          gold: goldGifts.data,
        });
      })
      .catch((err) => {
        this.messagingService.emit(GET_QUICK_GIFTS_DATA_ERROR, err);
      });
  };

  _getSortedGifts = ({ sortOption, page }) => {
    let URL = GIFTS_URL;
    switch (sortOption) {
      case 'price-low-high':
        URL = `${URL}?sort=price,asc&page=${page}`;
        break;
      case 'price-high-low':
        URL = `${URL}?sort=price,desc&page=${page}`;
        break;
      case 'popular':
        URL = `${URL}/popular`;
        break;
      case 'releaseDate':
        URL = `${URL}?sort=releaseDate,desc&page=${page}`;
        break;
      default:
        logger.error({ msg: 'unknown sort option:', error: sortOption });
    }

    ApiService.get(URL)
      .then((res) => {
        this.messagingService.emit(GET_SORTED_GIFTS_RESPONSE, res.data);
      })
      .catch((err) => {
        this.messagingService.emit(GET_SORTED_GIFTS_ERROR, err);
      });
  };

  getGiftingHistory = (params, impersonatedUser) => {
    const url = ApiService.substitute(GIFTING_HISTORY_URL, { username: encodeURIComponent(impersonatedUser) });
    const queryParams = {
      page: params.page,
      size: params.perPage,
    };
    ApiService.get(url, { params: queryParams })
      .then((res) => this.messagingService.emit(GIFTING_HISTORY_RESPONSE, res.data))
      .catch(() => this.messagingService.emit(GENERIC_ERROR));
  };

  searchGifts = (params) => {
    ApiService.get(GIFTS_SEARCH_URL, { params })
      .then((res) => this.messagingService.emit(GIFTS_SEARCH_RESPONSE, res.data))
      .catch(() => {
        this.messagingService.emit(GIFTS_SEARCH_ERROR);
      });
  };
}

export default new GiftsService();
